// Ритм покупок: как часто семья покупает товар и когда он обычно заканчивается. Метки «полезное / вредное».
import { z } from "zod";

/** По двум покупкам «обычно» не скажешь: ритм — от трёх разных дней. */
export const RHYTHM_MIN_PURCHASES = 3;
/** Смотрим последние промежутки: привычки меняются. */
const RHYTHM_WINDOW = 6;
/** Реже раза в полтора месяца — это не продукт, который «заканчивается»: прогноз не нужен. */
export const RHYTHM_MAX_DAYS = 45;
/** «Скоро закончится» — за столько дней до обычной покупки (и позже). */
export const RHYTHM_SOON_DAYS = 1;

export interface PurchaseRhythm {
  /** Сколько раз покупали (разные дни). */
  count: number;
  /** Последняя покупка, YYYY-MM-DD. */
  lastDay: string;
  /** Обычно покупают раз в столько дней (медиана последних промежутков). */
  everyDays: number;
  /** Когда обычно покупают снова, YYYY-MM-DD. */
  nextDay: string;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

function addDaysIso(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Ритм по дням покупок (в любом порядке, с повторами). null — ритма нет: покупок мало, покупают реже раза
 * в полтора месяца или давно перестали (втрое дольше обычного) — тогда и не напоминаем.
 */
export function purchaseRhythm(days: string[], today: string): PurchaseRhythm | null {
  const unique = [...new Set(days)].filter((d) => d <= today).sort();
  if (unique.length < RHYTHM_MIN_PURCHASES) return null;
  const gaps = unique
    .slice(1)
    .map((d, i) => daysBetween(unique[i]!, d))
    .slice(-RHYTHM_WINDOW);
  const everyDays = Math.max(1, Math.round(median(gaps)));
  if (everyDays > RHYTHM_MAX_DAYS) return null;
  const lastDay = unique.at(-1)!;
  if (daysBetween(lastDay, today) > everyDays * 3) return null;
  return { count: unique.length, lastDay, everyDays, nextDay: addDaysIso(lastDay, everyDays) };
}

/**
 * Ритм с последней покупкой, известной не только по операциям: продукт добавили в холодильник вручную —
 * отсчёт «когда снова» идёт от этой даты, если она позже.
 */
export function rhythmFrom(rhythm: PurchaseRhythm, lastDay: string): PurchaseRhythm {
  if (lastDay <= rhythm.lastDay) return rhythm;
  return { ...rhythm, lastDay, nextDay: addDaysIso(lastDay, rhythm.everyDays) };
}

/** Через сколько дней обычно покупают снова: 0 и меньше — пора (уже должно было закончиться). */
export function rhythmDueIn(rhythm: PurchaseRhythm, today: string): number {
  return daysBetween(today, rhythm.nextDay);
}

/** Скоро закончится или уже пора докупить. Купленное сегодня — нет: хлеб «каждый день» только что взяли. */
export function rhythmSoon(rhythm: PurchaseRhythm, today: string): boolean {
  return rhythmDueIn(rhythm, today) <= RHYTHM_SOON_DAYS && daysBetween(rhythm.lastDay, today) >= 1;
}

/** Метка товара: полезное, обычное, вредное (сладкое, газировка, чипсы, колбаса…). */
export const HEALTH_LABELS = ["healthy", "neutral", "unhealthy"] as const;
export type HealthLabel = (typeof HEALTH_LABELS)[number];

export const HealthLabelSchema = z.object({ health: z.enum(HEALTH_LABELS) });

/** Подсказка для списка покупок: закончилось (отметили в холодильнике) или подошёл обычный срок покупки. */
export interface ShoppingSuggestionDto {
  productId: string;
  name: string;
  /** Код категории расходов товара: в списке аптеки — «Здоровье», в детском — «Дети», в продуктовом — остальное. */
  category: string;
  reason: "used" | "due";
  /** Обычно покупают раз в столько дней; null — ритма нет (только «закончилось»). */
  everyDays: number | null;
  /** Через сколько дней обычно покупают снова (0 и меньше — пора). */
  dueIn: number | null;
}
